import React, { useMemo } from 'react';
import { RichTextBlock } from 'prismic-reactjs';
import { ImageProps } from '@components/image';
import Text from '@components/text';
import getLinkConfig from '@helpers/get-link-config';
import Wrapper from '@components/wrapper.styled';
import AnimatedElement from '@components/animated-element';
import {
  Content,
  Header,
  LinkButton,
  StyledImage,
  TextContent,
  StyledWrapper,
  ImageWrapper,
} from './photography-hero.styled';

export interface LinkProps {
  url?: string;
  uid?: string;
  target?: string;
}

export interface PhotographyHeroProps {
  image?: ImageProps;
  title?: string;
  subheading?: RichTextBlock[];
  linkLabel?: string;
  link?: LinkProps;
}

const DEFAULT_LINK_LABEL = 'Request a Demo';

const PhotographyHero: React.FC<PhotographyHeroProps> = ({
  image,
  title,
  subheading = [],
  linkLabel,
  link,
}) => {
  if (!title && !image && !subheading.length) {
    return null;
  }

  const linkConfig = useMemo(() => getLinkConfig(link), [link]);
  const hasImage = useMemo(() => !!(image?.gatsbyImageData || image?.url), [image]);

  const TextBlocks = useMemo(
    () => (
      <TextContent>
        {title && (
          <Text
            fontSize="heading1"
            type="title"
            tag="h1"
            stringText={title}
            fontColor="white"
            margin="none"
          />
        )}

        {subheading.length > 0 && (
          <Text type="subheading" richText={subheading} fontColor="black" margin="none" />
        )}
      </TextContent>
    ),
    [title, subheading]
  );

  return (
    <Header className="page-hero hero-primary" role="banner" aria-label={title || 'Page Hero'}>
      <Wrapper>
        <StyledWrapper>
          <Content image={image}>
            {TextBlocks}
            {linkConfig.url !== '' && (
              <LinkButton
                type="button"
                url={linkConfig.url}
                title={linkLabel || DEFAULT_LINK_LABEL}
                isExternal={linkConfig.isExternal}
                target={link?.target || '_self'}
                aria-label={linkLabel || DEFAULT_LINK_LABEL}
              />
            )}
          </Content>

          <ImageWrapper>
            {hasImage && (
              <AnimatedElement
                animationType="slideInRight"
                delay={0}
                threshold={0.1}
                rootMargin="50px"
                className="animate-wrapper"
              >
                <StyledImage
                  gatsbyImageData={image?.gatsbyImageData}
                  url={image?.url}
                  alt={image?.alt || title || 'Hero image'}
                  objectFit="cover"
                  loading="eager"
                />
              </AnimatedElement>
            )}
          </ImageWrapper>
        </StyledWrapper>
      </Wrapper>
    </Header>
  );
};

PhotographyHero.defaultProps = {
  subheading: [],
};

export default React.memo(PhotographyHero);
